import React, { useState, useEffect, useMemo } from "react";

import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";

import Moment from "react-moment";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import DataTable from "examples/Tables/DataTable";
import EventCalendar from "examples/Calendar";

import { getStage } from "../../../api/stages";

import CreateEditStages from "../CreateEditStages";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

import { useSelector } from "react-redux";
import { canEditOrder } from "../../../utils/permissions";

export default function StageTable({ device, user }) {
  const currentOrder = useSelector((state) => state.orders.currentOrder);
  const canEdit = canEditOrder(user, currentOrder);
  const [isLoading, setIsLoading] = useState(true);
  const [stages, setStages] = useState([]);
  const [stageRows, setStageRows] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [earliestStartDate, setEarliestStartDate] = useState(null);

  const stageColumns = [
    { Header: "Phase", accessor: "stage", align: "center" },
    { Header: "Description", accessor: "description", align: "center" },
    { Header: "Date de début", accessor: "start", align: "center" },
    { Header: "Date de fin", accessor: "end", align: "center" },
    { Header: "Nombre de jours", accessor: "number", align: "center" },
    { Header: "Responsable chantier", accessor: "ressource1", align: "center" },
    { Header: "Ressource 2", accessor: "ressource2", align: "center" },
    { Header: "Ressource 3", accessor: "ressource3", align: "center" },
    { Header: "Ressource 4", accessor: "ressource4", align: "center" },
  ];

  async function fetchStages() {
    try {
      setIsLoading(true); // Start loading
      const response = await getStage(device.device_id);
      setStages(response.data);
    } catch (error) {
      console.error("Error fetching stages:", error);
      // Optionally handle the error more gracefully here
    } finally {
      setIsLoading(false); // End loading
    }
  }

  useEffect(() => {
    fetchStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]); // Add device to dependency array

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  useEffect(() => {
    setStageRows(generateRows);
    const { events, earliestStartDate } = generateEvents();
    setCalendarEvents(events);
    setEarliestStartDate(earliestStartDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages, darkMode]);

  function generateRows() {
    const rows = [];
    for (let i = 0; i < stages.length; i++) {
      rows.push({
        stage: (
          <MDTypography component="a" variant="button" fontWeight="medium">
            {i + 1}
          </MDTypography>
        ),
        description: (
          <MDBox ml={-1}>
            {stages && stages[i].stage_description !== null ? (
              <MDBadge
                badgeContent={stages && stages[i].stage_description}
                color="info"
                variant="gradient"
                size="lg"
              />
            ) : (
              <MDBadge
                badgeContent="Non spécifié"
                color={!darkMode ? "dark" : "light"}
                variant="gradient"
                size="lg"
              />
            )}
          </MDBox>
        ),
        start: (
          <MDBox display="flex" alignItems="center" flexDirection="column">
            {stages && stages[i].stage_start_date !== null ? (
              <>
                <MDTypography component="span" variant="caption">
                  Débute le
                </MDTypography>
                <MDTypography component="span" fontWeight="medium" variant="button">
                  <Moment format="DD/MM/YYYY">{stages[i].stage_start_date}</Moment>
                </MDTypography>
              </>
            ) : (
              <MDTypography component="span" variant="caption" fontWeight="medium">
                Date non spécifiée
              </MDTypography>
            )}
          </MDBox>
        ),
        end: (
          <MDBox display="flex" alignItems="center" flexDirection="column">
            {stages && stages[i].stage_end_date !== null ? (
              <>
                <MDTypography component="span" variant="caption">
                  S'achève le
                </MDTypography>
                <MDTypography component="span" fontWeight="medium" variant="button">
                  <Moment format="DD/MM/YYYY">{stages[i].stage_end_date}</Moment>
                </MDTypography>
              </>
            ) : (
              <MDTypography component="span" variant="caption" fontWeight="medium">
                Date non spécifiée
              </MDTypography>
            )}
          </MDBox>
        ),
        number: (
          <MDBox display="flex" alignItems="center" flexDirection="column">
            {stages && stages[i].stage_duration !== null ? (
              <>
                <MDTypography component="span" variant="caption">
                  Comporte{" "}
                  <MDTypography component="span" fontWeight="medium" variant="button">
                    {stages[i].stage_duration}
                  </MDTypography>
                </MDTypography>

                <MDTypography component="span" variant="caption">
                  jours ouvrés
                </MDTypography>
              </>
            ) : (
              <MDTypography component="span" variant="caption" fontWeight="medium">
                Nombre non spécifié
              </MDTypography>
            )}
          </MDBox>
        ),
        ressource1: (
          <MDBox display="flex" alignItems="center" lineHeight={1}>
            {stages && stages[i].ressource1_id ? (
              <MDBox lineHeight={1}>
                <MDTypography display="block" variant="button" fontWeight="medium">
                  {stages[i].ressource1_name}
                </MDTypography>
                {/* <MDTypography variant="caption">{stages[i].ressource1_email}</MDTypography>
                <br />
                <MDTypography variant="caption">{stages[i].ressource1_phone}</MDTypography> */}
              </MDBox>
            ) : (
              <MDTypography component="a" variant="caption" color="error" fontWeight="medium">
                Non spécifiée
              </MDTypography>
            )}
          </MDBox>
        ),
        ressource2: (
          <MDBox display="flex" alignItems="center" lineHeight={1}>
            {stages && stages[i].ressource2_id ? (
              <MDBox lineHeight={1}>
                <MDTypography display="block" variant="button" fontWeight="medium">
                  {stages[i].ressource2_name}
                </MDTypography>
                {/* <MDTypography variant="caption">{stages[i].ressource2_email}</MDTypography>
                <br />
                <MDTypography variant="caption">{stages[i].ressource2_phone}</MDTypography> */}
              </MDBox>
            ) : (
              <MDTypography component="a" variant="caption" color="error" fontWeight="medium">
                Non spécifiée
              </MDTypography>
            )}
          </MDBox>
        ),
        ressource3: (
          <MDBox display="flex" alignItems="center" lineHeight={1}>
            {stages && stages[i].ressource3_id ? (
              <MDBox lineHeight={1}>
                <MDTypography display="block" variant="button" fontWeight="medium">
                  {stages[i].ressource3_name}
                </MDTypography>
                {/* <MDTypography variant="caption">{stages[i].ressource3_email}</MDTypography>
                <br />
                <MDTypography variant="caption">{stages[i].ressource3_phone}</MDTypography> */}
              </MDBox>
            ) : (
              <MDTypography component="a" variant="caption" color="warning" fontWeight="medium">
                Non spécifiée
              </MDTypography>
            )}
          </MDBox>
        ),
        ressource4: (
          <MDBox display="flex" alignItems="center" lineHeight={1}>
            {stages && stages[i].ressource4_id ? (
              <MDBox lineHeight={1}>
                <MDTypography display="block" variant="button" fontWeight="medium">
                  {stages[i].ressource4_name}
                </MDTypography>
                {/* <MDTypography variant="caption">{stages[i].ressource4_email}</MDTypography>
                <br />
                <MDTypography variant="caption">{stages[i].ressource4_phone}</MDTypography> */}
              </MDBox>
            ) : (
              <MDTypography component="a" variant="caption" color="warning" fontWeight="medium">
                Non spécifiée
              </MDTypography>
            )}
          </MDBox>
        ),
      });
    }
    return rows;
  }

  function LoadingOverlay() {
    return (
      <MDBox
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 2, // Ensure it's above other content
        }}
      >
        <MDTypography variant="h5">Chargement des phases...</MDTypography>
      </MDBox>
    );
  }

  function generateEvents() {
    let endDate = "";
    const events = [];

    let earliestStartDateStr =
      stages.length > 0 ? formatDate(new Date(stages[0].stage_start_date)) : formatDate(new Date());

    for (let i = 0; i < stages.length; i++) {
      const startDate = new Date(stages[i].stage_start_date);
      if (startDate < new Date(earliestStartDateStr)) {
        earliestStartDateStr = formatDate(startDate);
      }

      endDate = new Date(
        stages[i].stage_end_date !== null && stages[i].stage_end_date.substring(0, 10)
      );
      endDate.setDate(endDate.getDate() + 1);

      events.push({
        title:
          stages[i].stage_description !== null
            ? `${i + 1} - ${stages[i].stage_description}`
            : "Description de la phase inconnue",
        start: stages[i].stage_start_date !== null && stages[i].stage_start_date.substring(0, 10),
        end:
          stages[i].stage_end_date !== null &&
          `${endDate.getFullYear()}-${
            endDate.getMonth() + 1 < 10 ? "0" + (endDate.getMonth() + 1) : endDate.getMonth() + 1
          }-${endDate.getDate() < 10 ? "0" + endDate.getDate() : endDate.getDate()}`,
        className: "success",
      });
    }
    return { events, earliestStartDate: earliestStartDateStr };
  }

  function formatDate(date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return (
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1.5}
          px={2}
          variant="gradient"
          borderRadius="lg"
          sx={{
            background: !darkMode ? "linear-gradient(195deg, #364c70, #273956)" : "white",
            boxShadow: darkMode ? "0 1px 4px 1px white" : "0 1px 4px 1px #344767",
            display: "flex",
            verticalAlign: "middle",
          }}
        >
          <MDTypography
            variant="h5"
            style={{
              width: "50%",
              lineHeight: "2.4rem",
              display: "tableCell",
              verticalAlign: "middle",
            }}
            color={darkMode ? "inherit" : "white"}
          >
            Phases du chantier{" "}
            <MDTypography
              component="a"
              variant="caption"
              color={darkMode ? "inherit" : "white"}
              fontSize="1rem"
              style={{
                display: "tableCell",
                verticalAlign: "middle",
              }}
            >
              (Consultables dans le calendrier ci-dessous)
            </MDTypography>
          </MDTypography>
          {canEdit && (
              <Grid style={{ textAlign: "right", width: "50%" }}>
                <CreateEditStages
                  edit={false}
                  device={device}
                  stages={stages}
                  setStages={setStages}
                />
                {stages && stages.length > 0 && (
                  <CreateEditStages edit device={device} stages={stages} setStages={setStages} />
                )}
              </Grid>
            )}
        </MDBox>

        {stageRows && stageRows.length === 0 ? (
          <MDBox pt={0.75} sx={{ textAlign: "center", paddingTop: 3 }}>
            <MDTypography
              component="a"
              variant="h5"
              color={darkMode ? "white" : "inherit"}
              style={{
                display: "tableCell",
                verticalAlign: "middle",
              }}
            >
              Les phases du chantier n'ont pas encore été déterminées
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox pt={0.75}>
            <DataTable
              table={{ columns: stageColumns, rows: stageRows }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
            />
          </MDBox>
        )}
        <MDBox
          position="relative" // Required for positioning the LoadingOverlay
        >
          {isLoading && <LoadingOverlay />}
          <Grid
            item
            xs={12}
            margin={4}
            sx={{
              height: "max-content",
            }}
          >
            {useMemo(
              () => (
                <EventCalendar
                  key={earliestStartDate} // This forces the component to remount when the date changes
                  initialView="dayGridMonth"
                  initialDate={earliestStartDate}
                  events={calendarEvents}
                  selectable
                />
              ),
              [calendarEvents, earliestStartDate] // Dependency array
            )}
          </Grid>
        </MDBox>
      </Card>
    </Grid>
  );
}
